/**
 * The two decisions `PrivateRoute` and `PublicRoute` share.
 *
 * They live here, as pure functions, for one reason: the wrappers guard 83 routes between them and
 * **they must agree**. `PrivateRoute` bounces to `/` when there is no session; `PublicRoute`
 * bounces away from `/` when there is one. If those two predicates ever drift apart, a user lands
 * in a redirect loop rather than on a page. One function, used by both, makes that impossible by
 * construction — and unlike the components it can be tested, since this repo's jsdom cannot load.
 */

export interface SessionAffiliate {
  id?: number;
}

/** Where an authenticated user goes when they arrive at a public route with nowhere else to be. */
export const DEFAULT_LANDING = '/create-leads';

/**
 * A usable session needs both halves: a token, and an affiliate that has finished loading.
 *
 * The affiliate matters because almost everything downstream reads `affiliate.id`. A token alone
 * means "signed in but not ready", which is indistinguishable from signed out as far as rendering
 * a protected page goes.
 */
export const hasSession = (token: string | null | undefined, affiliate: SessionAffiliate | null | undefined): boolean =>
  !!token && !!affiliate?.id;

export interface RedirectOrigin {
  pathname?: string;
  search?: string;
}

/**
 * Where to send an authenticated user who has landed on a public route.
 *
 * `from` is set by `PrivateRoute` when it bounces someone away from a page they asked for — it is
 * what makes a deep link survive a slow affiliate fetch. Falls back to `DEFAULT_LANDING`, and never
 * returns `/`, which is the public route itself and would loop.
 */
export const resolveLandingPath = (from: RedirectOrigin | null | undefined): string => {
  if (!from?.pathname || from.pathname === '/') {
    return DEFAULT_LANDING;
  }
  return `${from.pathname}${from.search ?? ''}`;
};
